import { runGeneratorSync } from "../utils/run-generator.ts";
import { cloneBitmapToWritable } from "./clone.ts";
import type { Bitmap } from "./types.ts";

function clipPixelChannel(pixel: number) {
	if (pixel < 0) {
		return 0;
	}
	if (pixel > 0xff) {
		return 0xff;
	}
	return Math.round(pixel);
}

function perPixelOperation(
	source: Bitmap,
	other: Bitmap,
	operation: (
		r1: number,
		g1: number,
		b1: number,
		r2: number,
		g2: number,
		b2: number,
	) => Readonly<[number, number, number]>,
) {
	const result = runGeneratorSync(cloneBitmapToWritable(source));
	const src = source.data;
	const oth = other.data;
	const dst = result.data;
	const total = source.width * source.height * 4;
	for (let i = 0; i < total; i += 4) {
		const r1 = src[i];
		const g1 = src[i + 1];
		const b1 = src[i + 2];
		const r2 = oth[i];
		const g2 = oth[i + 1];
		const b2 = oth[i + 2];
		const out = operation(r1, g1, b1, r2, g2, b2);
		dst[i] = out[0];
		dst[i + 1] = out[1];
		dst[i + 2] = out[2];
		dst[i + 3] = 0xff;
	}
	return result;
}

function pixelBrightness(r: number, g: number, b: number): number {
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const pixelMaxValue = 0xff;
const pixelMidValue = pixelMaxValue * 0.5;

/** @deprecated Only used by legacy detail filter, can be removed when that is */
function vividLightBlend(
	foregroundBitmap: Bitmap,
	background: Bitmap,
	amount: number,
) {
	return perPixelOperation(
		foregroundBitmap,
		background,
		(fR, fG, fB, bR, bG, bB) => {
			const fBrightness = pixelBrightness(fR, fG, fB);
			if (fBrightness <= pixelMidValue) {
				return [
					clipPixelChannel(0xff - 0xff * ((0xff - bR) / (amount * fR))),
					clipPixelChannel(0xff - 0xff * ((0xff - bG) / (amount * fG))),
					clipPixelChannel(0xff - 0xff * ((0xff - bB) / (amount * fB))),
				];
			}
			return [
				clipPixelChannel(0xff * (bR / (amount * (0xff - fR)))),
				clipPixelChannel(0xff * (bG / (amount * (0xff - fG)))),
				clipPixelChannel(0xff * (bB / (amount * (0xff - fB)))),
			];
		},
	);
}

/** @deprecated Only used by legacy detail filter, can be removed when that is */
function overlayBlend(foreground: Bitmap, background: Bitmap) {
	return perPixelOperation(foreground, background, (fR, fG, fB, bR, bG, bB) => {
		const bBrightness = pixelBrightness(bR, bG, bB);
		if (bBrightness <= pixelMidValue) {
			return [
				clipPixelChannel(0xff * ((((2 * bR) / 0xff) * fR) / 0xff)),
				clipPixelChannel(0xff * ((((2 * bG) / 0xff) * fG) / 0xff)),
				clipPixelChannel(0xff * ((((2 * bB) / 0xff) * fB) / 0xff)),
			];
		}

		return [
			clipPixelChannel(
				0xff - 0xff * ((((2 * (0xff - fR)) / 0xff) * (0xff - bR)) / 0xff),
			),
			clipPixelChannel(
				0xff - 0xff * ((((2 * (0xff - fG)) / 0xff) * (0xff - bG)) / 0xff),
			),
			clipPixelChannel(
				0xff - 0xff * ((((2 * (0xff - fB)) / 0xff) * (0xff - bB)) / 0xff),
			),
		];
	});
}

export { vividLightBlend, overlayBlend };
